import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import Modal from 'react-bootstrap/Modal';
import { AnchorComponent, ButtonComponent } from "../../../components/elements";
import { marketplaces, getActiveMarketplaceKeys } from "./marketplacesData";
import "./ProductTable.css";

const TickIcon = () => (
    <svg viewBox="0 0 24 24" fill="none">
        <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const DashIcon = () => (
    <svg viewBox="0 0 24 24" fill="none">
        <path d="M6 12h12" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
);

export default function ProductTable({ thead, tbody }) {
    const { t } = useContext(TranslatorContext);

    const [alertModal, setAlertModal] = useState(false);
    const [selectedProductId, setSelectedProductId] = useState(null);
    const [data, setData] = useState([]);

    useEffect(() => {
        setData(tbody || []);
    }, [tbody]);

    const handleCheckbox = (event) => {
        const { name, checked } = event.target;

        if (name === "allCheck") {
            const checkData = data?.map((item) => ({ ...item, isChecked: checked }));
            setData(checkData);
        } else {
            const checkData = data?.map((item, index) => {
                const checkName = `check${index}`;
                return checkName === name ? { ...item, isChecked: checked } : item;
            });
            setData(checkData);
        }
    };

    const handleDeleteClick = (id) => {
        setSelectedProductId(id);
        setAlertModal(true);
    };

    // Normalizes category into an array of display names, whether the API
    // sends a single string, a single object, or an array of either.
    const getCategoryList = (item) => {
        const raw = item?.category;
        const list = Array.isArray(raw) ? raw : [raw];
        return list
            .map((c) => (typeof c === "object" ? c?.name : c))
            .filter(Boolean);
    };

    return (
        <div className="mc-table-responsive wp-products-table">
            <table className="mc-table product">
                <thead className="mc-table-head primary">
                    <tr>
                        {thead?.map((item, index) => (
                            <th key={index}>{t(item)}</th>
                        ))}
                    </tr>
                </thead>
                <tbody className="mc-table-body even">
                    {data && data.length > 0 ? (
                        data.map((item, index) => {
                            // Safe extracts supporting both dynamic API resources & static data
                            const pImage = process.env.REACT_APP_IMG_URL + (item?.first_img || "");
                            const pTitle = item?.title || "N/A";
                            const pCode = item?.product_code || "N/A";
                            const style = item?.selling_style || "N/A";
                            const pQty = item?.product_qty || "N/A";

                            // Subtitle line under the title — was previously duplicating the
                            // style pill's value. Now prefers a real description/SKU instead.
                            const pSubtitle = item?.description || item?.sku || item?.short_description || "";

                            const categoryList = getCategoryList(item);

                            const pBrand = typeof item?.brand === "object"
                                ? item?.brand?.name
                                : (item?.brand || "N/A");

                            // Safe Price extractions
                            const pricePrev = typeof item?.price === "object" ? item?.price?.previous : null;
                            const pricePresent = typeof item?.price === "object"
                                ? item?.price?.present
                                : (item?.price || item?.buying_price || item?.sell_price_dollar || 0);

                            // Safe Action Icon extractions
                            const iconView = item?.action?.view || "visibility";
                            const iconEdit = item?.action?.edit || "edit";
                            const iconDelete = item?.action?.delete || "delete";

                            // Marketplace listing status (real data if present, dummy fallback otherwise)
                            const activeKeys = getActiveMarketplaceKeys(item);

                            return (
                                <tr key={item?.id || index}>
                                    <td data-label="code" title={index + 1}>
                                        <div className="mc-table-check">
                                            <span className="wp-code-pill">{pCode}</span>
                                        </div>
                                    </td>
                                    <td data-label="product">
                                        <div className="mc-table-product md">
                                            {pImage && <img src={pImage} alt={pTitle} />}
                                            <div className="mc-table-group">
                                                <h6 title={pTitle}>{pTitle}</h6>
                                                {pSubtitle && <p title={pSubtitle}>{pSubtitle}</p>}
                                            </div>
                                        </div>
                                    </td>

                                    <td data-label="category">
                                        <div className="wp-pill-group">
                                            {categoryList.length > 0
                                                ? categoryList.map((cat, i) => (
                                                    <span key={i} className="wp-pill wp-pill--category">{cat}</span>
                                                  ))
                                                : <span className="wp-pill wp-pill--category">N/A</span>}
                                        </div>
                                    </td>
                                    <td data-label="brand"><span className="wp-pill wp-pill--brand">{pBrand}</span></td>
                                    <td data-label="style"><span className="wp-pill wp-pill--style">{style}</span></td>
                                    <td data-label="price">
                                        <div className="mc-table-price">
                                            {pricePrev && <del>{pricePrev}</del>}
                                            <p>{pricePresent}</p>
                                        </div>
                                    </td>

                                    {/* Market Places */}
                                    <td data-label="marketplaces">
                                        <div className="wp-mp-list">
                                            {marketplaces.map((mp) => {
                                                const isActive = activeKeys.includes(mp.key);
                                                return (
                                                    <span
                                                        key={mp.key}
                                                        className={`wp-mp-badge ${isActive ? "is-active" : "is-inactive"}`}
                                                        style={isActive ? { "--mp-color": mp.color } : undefined}
                                                        title={`${mp.name} — ${isActive ? t("listed") || "Listed" : t("not_listed") || "Not listed"}`}
                                                    >
                                                        {mp.logo ? (
                                                            <img src={mp.logo} alt={mp.name} className="wp-mp-logo" />
                                                        ) : (
                                                            <span className="wp-mp-initial">{mp.name.charAt(0)}</span>
                                                        )}
                                                        <span className={`wp-mp-status ${isActive ? "is-tick" : "is-dash"}`}>
                                                            {isActive ? <TickIcon /> : <DashIcon />}
                                                        </span>
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    </td>

                                    <td data-label="actions">
                                        <div className="mc-table-action">
                                            <AnchorComponent to={`/product-view/${item?.id || ''}`} title="View" className="material-icons view">{iconView}</AnchorComponent>
                                            <AnchorComponent to={`/product-upload/${item?.id || ''}`} title="Edit" className="material-icons edit">{iconEdit}</AnchorComponent>
                                            <ButtonComponent type="button" title="Delete" className="material-icons delete" onClick={() => handleDeleteClick(item?.id)}>{iconDelete}</ButtonComponent>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })
                    ) : (
                        <tr>
                            <td colSpan={(thead?.length || 8) + 1} className="text-center py-4">
                                {t("no_products_found")}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>

            <Modal show={alertModal} onHide={() => setAlertModal(false)}>
                <div className="mc-alert-modal">
                    <i className="material-icons">new_releases</i>
                    <h3>{t("are_you_sure")}</h3>
                    <p>{t("want_to_delete_this_product")}</p>
                    <Modal.Footer>
                        <ButtonComponent type="button" className="btn btn-secondary" onClick={() => setAlertModal(false)}>{t('close')}</ButtonComponent>
                        <ButtonComponent type="button" className="btn btn-danger" onClick={() => setAlertModal(false)}>{t('delete')}</ButtonComponent>
                    </Modal.Footer>
                </div>
            </Modal>
        </div>
    );
}